import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomFile from '../../../components/common/CustomFile'
import CustomTextarea from '../../../components/common/CustomTextarea'
import * as Yup from 'yup'
import CustomSelect from '../../../components/common/CustomSelect'

// Validation
const validationSchema = Yup.object({
  logo: Yup.string().trim().required('Light Logo is Required'),
  darkLogo: Yup.string().trim().required('Dark Logo is Required'),
  tagline: Yup.string().trim().required('Tagline is required'),
  about: Yup.string().trim().required('About information is required'),
  history: Yup.string().trim().required('History is required'),
  vision: Yup.string().trim().required('Vision is required'),
  mission: Yup.string().trim().required('Mission is required'),
  contact: Yup.string().trim().required('Contact information is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  website: Yup.string().trim().required('Website is required'),
})

function Edit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)
  const [stepInfo, setStepInfo] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [allUsers, setAllUsers] = useState([])
  const [seriesOptions, setSeriesOptions] = useState([])


  useEffect(() => {
    switch (step) {
      case 1:
        setStepInfo('Company Branding Details')
        break

      case 2:
        setStepInfo('Social Media Links')
        break

      case 3:
        setStepInfo('Catalogue Details')
        break

      default:
        setStepInfo('')
    }
  }, [step])

  const nextStep = () => {
    setStep((prev) => (prev < 3 ? prev + 1 : prev))
  }

  const prevStep = () => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev))
  }

  const formik = useFormik({
    initialValues: {
      company: id,
      logo: null,
      darkLogo: null,
      tagline: '',
      about: '',
      history: '',
      vision: '',
      mission: '',
      contact: '',
      email: '',
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      website: '',
      // series_logo: null,
      // about_series: '',
      series: [],
      username: '',
      password: '',
      confirm_password: '',
      is_sm: false,
      sm_post: '',
      sm_post_user: [],
      sm_reel: '',
      sm_reel_user: [],
      sm_walk: '',
      sm_walk_user: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  const fetchData = async () => {
    try {
      const response = await axiosAuthInstance.get(
        `company-branding/detail/${id}`,
      )
      if (response?.data && response?.data?.result) {
        const data = response.data.result
        formik.setValues({
          company: data.company._id,
          logo: data.logo,
          darkLogo: data.darkLogo,
          tagline: data.tagline,
          about: data.about,
          history: data.history,
          vision: data.vision,
          mission: data.mission,
          contact: data.contact,
          email: data.email,
          website: data.website,
          facebook: data.facebook,
          twitter: data.twitter,
          linkedin: data.linkedin,
          instagram: data.instagram,
          website: data.website,
          series: data?.series?.map((item) => (item._id)),
          // series_logo: data.series_logo,
          // about_series: data.about_series,
          is_sm: data.is_sm,
          sm_post: data?.sm_post,
          sm_post_user: data?.sm_post_user?.map((item) => item._id),
          sm_reel_user: data?.sm_reel_user?.map((item) => item._id),
          sm_walk_user: data?.sm_walk_user?.map((item) => item._id),
          sm_reel: data?.sm_reel,
          sm_walk: data?.sm_walk,
        })
        setLoading(false)
      } else {
        navigate('/company-branding')
      }
    } catch (error) {
      console.log('error in fetching edit data: ', error)
      setLoading(false)
    }
  }

  const fetchAllUsers = async () => {
    try {
      const response = await axiosAuthInstance.get('team/dropdown', {
        isAll: true,
      })
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.full_name} (${item.role})`,
        }))
        setAllUsers(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const getSeries = async () => {
    try {
      const response = await axiosAuthInstance.get(`series/dropdown/${id}`)
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.name}`,
        }))
        setSeriesOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchAllUsers()
  }, [])

  useEffect(() => {
    fetchData()
    getSeries()
  }, [id])

  const handleSubmit = async (values, { resetForm }) => {
    setIsLoading(true)
    try {
      const formData = new FormData()

      Object.keys(values).forEach((key) => {
        if (
          key !== 'logo' &&
          key !== 'darkLogo' &&
          key !== 'series' &&
          key !== 'sm_post_user' &&
          key !== 'sm_reel_user' &&
          key !== 'sm_walk_user'
        ) {
          formData.append(key, values[key])
        }
      })

      if (values?.sm_post_user?.length > 0) {
        formData.append('sm_post_user', JSON.stringify(values.sm_post_user))
      }

      if (values?.sm_reel_user?.length > 0) {
        formData.append('sm_reel_user', JSON.stringify(values.sm_reel_user))
      }

      if (values?.sm_walk_user?.length > 0) {
        formData.append('sm_walk_user', JSON.stringify(values.sm_walk_user))
      }

      if (values?.series?.length > 0) {
        formData.append('series', JSON.stringify(values.series))
      }

      // Helper function to check if the value is a binary file (File or Blob)
      const isBinaryFile = (file) =>
        file instanceof File || file instanceof Blob

      // Handle single file fields only if they are binary (not URLs/strings)
      if (isBinaryFile(values.logo)) {
        formData.append('logo', values.logo)
      }

      if (isBinaryFile(values.darkLogo)) {
        formData.append('darkLogo', values.darkLogo)
      }

      if (isBinaryFile(values.series_logo)) {
        formData.append('series_logo', values.series_logo)
      }

      // Make the API request
      const response = await axiosAuthInstance.put(
        `company-branding/update/${id}`,
        formData,
      )
      if (response && response.status === 200) {
        navigate('/company-branding')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <FormikProvider value={formik}>
      <form
        className='flex flex-col p-4 gap-4'
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div>
          <label className='bg-blue-100 text-blue-800 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-900 dark:text-white'>
            Step: {step}
          </label>
          {stepInfo && (
            <label className='bg-blue-100 text-blue-800 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-900 dark:text-white'>
              {stepInfo}
            </label>
          )}
        </div>

        {step === 1 && (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='logo'
                accept='image/*'
                label='Upload Logo (Light)'
                required
                component={CustomFile}
                url={formik.values.logo}
              />

              <Field
                name='darkLogo'
                accept='image/*'
                label='Dark Logo'
                required
                component={CustomFile}
                url={formik.values.darkLogo}
              />
            </div>

            <Field
              name='tagline'
              label='Tagline'
              placeholder='Tagline'
              required
              component={CustomInput}
            />

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='about'
                label='About'
                placeholder='About'
                required
                component={CustomTextarea}
              />

              <Field
                name='history'
                label='History'
                placeholder='History'
                required
                component={CustomTextarea}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='mission'
                label='Mission'
                placeholder='Mission'
                required
                component={CustomTextarea}
              />

              <Field
                name='vision'
                label='Vision'
                placeholder='Vision'
                required
                component={CustomTextarea}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='contact'
                label='Contact'
                placeholder='Contact'
                required
                component={CustomInput}
              />

              <Field
                name='email'
                label='Email'
                placeholder='Email'
                required
                component={CustomInput}
              />
            </div>
            <Field
              name='website'
              label='Website'
              placeholder='Website'
              required
              component={CustomInput}
            />
            {/* <Field
              name="is_sm"
              label="Social Media"
              placeholder="Social Media"
              component={CustomCheckBox}
              checked={formik.values.is_sm}
            />
            {formik.values.is_sm && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_post"
                    label="Social Media Post"
                    type="Number"
                    placeholder="Social Media Post"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_post_user"
                    label="Social Media Post User"
                    placeholder="Social Media Post User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_reel"
                    label="Social Media Reel"
                    placeholder="Social Media Reel"
                    type="Number"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_reel_user"
                    label="Social Media Reel User"
                    placeholder="Social Media Reel User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_walk"
                    label="Social Media Walk Through"
                    placeholder="Social  Walk Through"
                    type="Number"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_walk_user"
                    label="Social Media Walk Through User"
                    placeholder="Social Media Walk Through User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
              </>
            )} */}
          </>
        )}

        {step === 2 && (
          <>
            <Field
              name='facebook'
              label='Facebook'
              placeholder='Facebook'
              component={CustomInput}
            />

            <Field
              name='twitter'
              label='Twitter'
              placeholder='Twitter'
              component={CustomInput}
            />

            <Field
              name='linkedin'
              label='Linkedin'
              placeholder='Linkedin'
              component={CustomInput}
            />

            <Field
              name='instagram'
              label='Instagram'
              placeholder='Instagram'
              component={CustomInput}
            />
          </>
        )}

        {step === 3 && (
          <>
            <Field
              name="series"
              label="Series"
              placeholder="Series"
              component={CustomSelect}
              options={seriesOptions}
              isMulti
            />
          </>
        )}

        <div className='flex justify-end'>
          {step !== 1 && (
            <button
              type='button'
              onClick={prevStep}
              className='bg-gray-500 hover:bg-white text-xl font-medium mx-2 text-white hover:text-gray-500 border border-gray-500 rounded-lg px-6 py-2'
            >
              Previous
            </button>
          )}

          {step !== 3 && (
            <button
              type='button'
              onClick={nextStep}
              className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-gray-500 rounded-lg px-6 py-2'
            >
              Next
            </button>
          )}

          {step === 3 && (
            <button
              type='submit'
              disabled={isLoading}
              className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-gray-500 rounded-lg px-6 py-2'
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </form>
    </FormikProvider>
  )
}

export default Edit
